import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { MessageDto, UnreadCount } from './models';

@Injectable({ providedIn: 'root' })
export class MessageService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/messages`;

  /** Unread message counts keyed by the sending friend's user id. */
  readonly unread = signal<Map<string, number>>(new Map());
  readonly totalUnread = computed(() =>
    [...this.unread().values()].reduce((sum, n) => sum + n, 0)
  );

  send(toUserId: string, body: string): Promise<MessageDto> {
    return firstValueFrom(this.http.post<MessageDto>(this.base, { toUserId, body }));
  }

  /** The conversation with a friend. Loading it marks their messages as read. */
  conversation(userId: string): Promise<MessageDto[]> {
    return firstValueFrom(this.http.get<MessageDto[]>(`${this.base}/${userId}`));
  }

  /** Delete the credential in a shared-credential message (tombstones the body). */
  redact(messageId: string): Promise<MessageDto> {
    return firstValueFrom(this.http.post<MessageDto>(`${this.base}/${messageId}/redact`, {}));
  }

  async loadUnread(): Promise<void> {
    const counts = await firstValueFrom(this.http.get<UnreadCount[]>(`${this.base}/unread`));
    this.unread.set(new Map(counts.map((c) => [c.userId, c.count])));
  }

  unreadFrom(userId: string): number {
    return this.unread().get(userId) ?? 0;
  }
}
